import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps a garment and its paid add-on lines telling the same story.
 *
 * In `line_item` mode an add-on is its own cart line (see assets/product-addons.js —
 * a line item property cannot carry money), so the cart can drift into two states
 * that are wrong in opposite directions:
 *
 *   1. The garment is removed and „Добавка: Шапка €8,00" stays behind. The customer
 *      is charged €8 for a hat attached to nothing.
 *   2. The add-on line is removed and the garment keeps „Шапка: Да". The order tells
 *      production to include a hat that nobody paid for.
 *
 * This fixes both, in that order, after every cart change.
 *
 * ── Why it is allowed to touch a live cart ───────────────────────────────────
 *
 * Deleting from a customer's cart is worse than the problem if it is wrong, so
 * nothing here is heuristic. Three keys, all written only by product-addons.js and
 * by nothing else on this store:
 *
 *   * `_addon_group` on a garment  — the pairing id
 *   * `_addon_of`    on an add-on  — the garment's pairing id
 *   * `_addon_key`   on an add-on  — which property on the garment it paid for
 *   * `_addon_keys`  on a garment  — the property keys that are add-ons, so an
 *                                    ordinary option like „Кройка" can never be
 *                                    mistaken for one
 *
 * A line is removed only when it carries `_addon_of` and no line in the cart carries
 * a matching `_addon_group`. A property is stripped only when it is named in that
 * garment's own `_addon_keys`. Anything unexpected — a missing key, a bad response,
 * a shape that is not what it should be — does nothing at all.
 */
class CartAddonGuard extends HTMLElement {
  #running = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#check);
    // Also on connect: the garment may have been removed in a previous visit or in
    // another tab, so the cart can already be inconsistent when this first renders.
    this.#check();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#check);
  }

  /** @param {Event & {promise?: Promise<unknown>}} [event] */
  #check = (event) => {
    if (event && event.target === this) return; // our own re-announce, below
    if (this.#running) return;
    this.#running = true;

    // Wait for the change that triggered this to actually commit — but never
    // indefinitely.
    //
    // The event is dispatched BEFORE the cart mutation resolves, so reading
    // `/cart.js` straight away returns the pre-removal cart: the add-on still looks
    // paid for and the garment keeps „Ключодържател: Да" for free. Awaiting the
    // event's promise fixes that — and introduces a worse failure, because a promise
    // that never settles leaves `#running` true and wedges the guard for the rest of
    // the session. Seen exactly that: one removal reconciled correctly, the next did
    // nothing, and a manually re-fired event did nothing either while the logic
    // itself checked out against the live cart.
    //
    // So the wait is a race. Losing it costs one early read, and the reconcile is
    // driven by `/cart.js` rather than by the event, so a late-settling change simply
    // gets picked up by the next one.
    const committed = Promise.resolve(event?.promise).catch(() => {});
    const deadline = new Promise((resolve) => setTimeout(resolve, 1500));

    Promise.race([committed, deadline])
      .then(() => this.#reconcile())
      .catch((error) => console.warn('[cart-addons] reconcile skipped:', error))
      .finally(() => {
        this.#running = false;
      });
  };

  /**
   * POST to a cart endpoint and return the parsed body.
   *
   * `fetchConfig('json', …)`, NOT `'javascript'`: the javascript variant deletes
   * Content-Type because it is meant for FormData, so a JSON string body arrives
   * unparseable, Shopify answers 400 with an EMPTY body, and `.json()` then throws
   * before any status check can report what went wrong. That is exactly how the
   * first version of this file failed — silently, with the orphan still in the cart.
   *
   * @param {string} url
   * @param {object} body
   */
  async #post(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const text = await response.text();
    /** @type {any} */
    let parsed = {};
    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`${url} returned ${response.status} with an unparseable body`);
    }
    if (!response.ok || parsed.status) {
      throw new Error(parsed.description || parsed.message || `${url} returned ${response.status}`);
    }
    return parsed;
  }

  async #cart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const text = await response.text();
    if (!response.ok || !text) throw new Error(`cart.js returned ${response.status}`);
    return JSON.parse(text);
  }

  async #reconcile() {
    const removed = await this.#removeOrphans();
    const stripped = await this.#stripPaidForProperties();
    if (removed || stripped) await this.#announce();
  }

  /** @returns {Promise<boolean>} whether anything was removed */
  async #removeOrphans() {
    let removed = false;

    // One removal per pass, re-reading each time: `line` is 1-based and every
    // remaining line number shifts the moment one goes, so a list collected up
    // front would be stale by the second deletion.
    for (let pass = 0; pass < 20; pass++) {
      const cart = await this.#cart();
      const items = Array.isArray(cart?.items) ? cart.items : null;
      if (!items) return removed;

      const groups = new Set(
        items.map((item) => item?.properties?._addon_group).filter((id) => typeof id === 'string' && id !== '')
      );
      const orphan = items.findIndex((item) => {
        const of = item?.properties?._addon_of;
        return typeof of === 'string' && of !== '' && !groups.has(of);
      });
      if (orphan === -1) return removed;

      await this.#post(Theme.routes.cart_change_url, { line: orphan + 1, quantity: 0 });
      removed = true;
    }
    return removed;
  }

  /**
   * Strips an add-on property from a garment whose add-on line has been removed.
   *
   * ── Two things that bit here ────────────────────────────────────────────────
   *
   * **A property is cleared by setting it to an empty string, not by omitting it.**
   * `/cart/change.js` merges the `properties` object rather than replacing it, so
   * sending everything-except-the-one-to-drop leaves the cart exactly as it was.
   *
   * **And the loop has to notice when a write changed nothing.** The first version
   * re-ran its predicate after each write and, because the write was a no-op, found
   * the same line again — twenty passes per cart event, on every cart event. It
   * issued 209 requests to `/cart/change` before anyone looked at the network tab.
   * A loop that repeats an action until a condition clears must also give up when
   * the action stops clearing it; `handled` is that.
   *
   * @returns {Promise<boolean>} whether anything was stripped
   */
  async #stripPaidForProperties() {
    let stripped = false;
    /** Line keys already written in this sweep — see the note above. */
    const handled = new Set();

    for (let pass = 0; pass < 20; pass++) {
      const cart = await this.#cart();
      const items = Array.isArray(cart?.items) ? cart.items : null;
      if (!items) return stripped;

      // Which add-on keys are still paid for, per garment group.
      /** @type {Map<string, Set<string>>} */
      const paidFor = new Map();
      for (const item of items) {
        const of = item?.properties?._addon_of;
        const key = item?.properties?._addon_key;
        if (typeof of !== 'string' || !of || typeof key !== 'string' || !key) continue;
        if (!paidFor.has(of)) paidFor.set(of, new Set());
        paidFor.get(of)?.add(key);
      }

      const index = items.findIndex((item) => {
        if (handled.has(item.key)) return false;
        const group = item?.properties?._addon_group;
        const declared = item?.properties?._addon_keys;
        if (typeof group !== 'string' || !group || typeof declared !== 'string' || !declared) return false;
        const still = paidFor.get(group) ?? new Set();
        // Only keys this garment itself declared as add-ons, so an ordinary variant
        // property can never be caught by this. An already-cleared key is an empty
        // string, which must read as absent rather than as a value to clear again.
        return declared.split('|').some((key) => key && !still.has(key) && item.properties[key]);
      });
      if (index === -1) return stripped;

      const item = items[index];
      handled.add(item.key);

      const group = item.properties._addon_group;
      const still = paidFor.get(group) ?? new Set();
      const declared = String(item.properties._addon_keys).split('|').filter(Boolean);
      const remaining = declared.filter((key) => still.has(key));

      /** @type {Record<string, string>} */
      const changes = {};
      for (const key of declared) {
        if (!still.has(key)) changes[key] = '';
      }
      // Nothing left to pair, so the garment stops being part of a group at all and
      // can merge with an identical shirt again.
      changes._addon_keys = remaining.join('|');
      if (remaining.length === 0) changes._addon_group = '';

      await this.#post(Theme.routes.cart_change_url, {
        id: item.key,
        properties: { ...item.properties, ...changes },
      });
      stripped = true;
    }
    return stripped;
  }

  /**
   * Re-render the cart from one payload. Without this the line is gone server-side
   * but still on screen, and the totals disagree with the server — which looks
   * exactly like the bug this exists to prevent.
   */
  async #announce() {
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const url = new URL(`${Theme.routes.cart_url}.js`, window.location.origin);
    if (sections.length) url.searchParams.set('sections', sections.join(','));
    const cart = await fetch(url, { headers: { Accept: 'application/json' } }).then((res) => res.json());

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-addon-guard',
        sourceId: 'cart-addon-guard',
        itemCount: cart.item_count,
        sections: cart.sections,
        didError: false,
      },
    });
  }
}

if (!customElements.get('cart-addon-guard')) {
  customElements.define('cart-addon-guard', CartAddonGuard);
}
